import React, { useState, useMemo } from 'react';
import { 
  TrendingUp, 
  CreditCard, 
  DollarSign, 
  Shield, 
  AlertTriangle, 
  CheckCircle,
  User,
  Building2,
  PieChart,
  BarChart3,
  Calendar,
  FileText
} from 'lucide-react';
import { MetricCard } from './MetricCard';
import { 
  BuyerFinancialProfile, 
  sampleBuyerFinancialProfile, 
  calculateBuyerFinancialMetrics,
  formatCurrency,
  formatPercentage,
  getCreditScoreColor,
  getRiskLevelColor
} from '../types/buyerFinancialData';

export function BuyerFinancialAnalysisDebug() {
  const [selectedTimeframe, setSelectedTimeframe] = useState<'current' | 'historical'>('current');
  
  console.log('BuyerFinancialAnalysisDebug: Component starting to render');
  
  try {
    // Calculate metrics from sample data
    const buyerProfile = useMemo(() => {
      console.log('BuyerFinancialAnalysisDebug: Calculating buyer profile');
      return calculateBuyerFinancialMetrics(sampleBuyerFinancialProfile);
    }, []);

    console.log('BuyerFinancialAnalysisDebug: Buyer profile calculated:', buyerProfile);

    // Calculate key metrics
    const averageCreditScore = buyerProfile.creditScores.reduce((sum, score) => sum + score.score, 0) / buyerProfile.creditScores.length;
    const creditScoreRange = averageCreditScore >= 750 ? 'excellent' : averageCreditScore >= 700 ? 'good' : averageCreditScore >= 650 ? 'fair' : 'poor';
    
    const liquidAssets = buyerProfile.bankAccounts.reduce((sum, account) => sum + account.currentBalance, 0);
    const totalDebtPayments = buyerProfile.sbaPersonalFinancialStatement.liabilities.reduce((sum, liability) => sum + liability.monthlyPayment, 0);
    
    // Calculate SBA-specific metrics
    const sbaLiquidityRequirement = buyerProfile.monthlyExpenses * 3; // 3 months of expenses
    const meetsLiquidityRequirement = liquidAssets >= sbaLiquidityRequirement;
    const sbaDebtToIncomeThreshold = 40; // SBA typically requires DTI < 40%
    const meetsDTIRequirement = buyerProfile.debtToIncomeRatio < sbaDebtToIncomeThreshold;

    console.log('BuyerFinancialAnalysisDebug: Metrics calculated, rendering component');

    return (
      <div className="py-4 px-4 sm:py-6 sm:px-6 lg:py-8 lg:px-8">
        {/* Header Section */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
            <User className="w-5 h-5 text-blue-600" />
            Buyer Financial Profile Analysis - Debug
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            Comprehensive analysis of buyer's financial position for SBA loan qualification assessment.
            Based on SBA Personal Financial Statement, Tax Returns, Bank Statements, and Credit Reports.
          </p>
        </div>

        {/* Key Financial Metrics */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-green-600" />
            Key Financial Metrics
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            <MetricCard
              title="Net Worth"
              value={buyerProfile.netWorth}
              className="border-l-4 border-l-blue-500"
              sublabel={`Assets: ${formatCurrency(buyerProfile.totalAssets)} | Liabilities: ${formatCurrency(buyerProfile.totalLiabilities)}`}
            />
            <MetricCard
              title="Monthly Income"
              value={buyerProfile.monthlyIncome}
              className="border-l-4 border-l-green-500"
              sublabel={`Annual: ${formatCurrency(buyerProfile.monthlyIncome * 12)}`}
            />
            <MetricCard
              title="Debt-to-Income Ratio"
              value={buyerProfile.debtToIncomeRatio}
              format="percentage"
              className={`border-l-4 ${meetsDTIRequirement ? 'border-l-green-500' : 'border-l-red-500'}`}
              sublabel={`SBA Requirement: &lt; ${sbaDebtToIncomeThreshold}%`}
            />
            <MetricCard
              title="Liquidity Ratio"
              value={buyerProfile.liquidityRatio}
              format="decimal"
              className={`border-l-4 ${meetsLiquidityRequirement ? 'border-l-green-500' : 'border-l-yellow-500'}`}
              sublabel={`Months of expenses covered`}
            />
          </div>
        </div>

        {/* Simple Credit Analysis without charts */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-purple-600" />
            Credit Analysis
          </h3>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h4 className="font-semibold text-gray-900 mb-4">Credit Scores by Bureau</h4>
            <div className="space-y-3">
              {buyerProfile.creditScores.map((score) => (
                <div key={score.bureau} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <div className="font-medium text-gray-900">{score.bureau}</div>
                    <div className="text-sm text-gray-600">Updated: {new Date(score.date).toLocaleDateString()}</div>
                  </div>
                  <div className={`text-2xl font-bold ${getCreditScoreColor(score.score)}`}>
                    {score.score}
                  </div>
                </div>
              ))}
              <div className="pt-3 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="font-medium text-gray-900">Average Score</div>
                  <div className={`text-2xl font-bold ${getCreditScoreColor(averageCreditScore)}`}>
                    {Math.round(averageCreditScore)}
                  </div>
                </div>
                <div className="text-sm text-gray-600 mt-1">
                  Range: {creditScoreRange.charAt(0).toUpperCase() + creditScoreRange.slice(1)}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Debug Info */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h4 className="font-semibold text-yellow-900 mb-2">Debug Information</h4>
          <div className="text-sm text-yellow-800">
            <p>Component rendered successfully!</p>
            <p>Buyer Profile ID: {buyerProfile.id}</p>
            <p>Credit Scores Count: {buyerProfile.creditScores.length}</p>
            <p>Bank Accounts Count: {buyerProfile.bankAccounts.length}</p>
            <p>Assets Count: {buyerProfile.sbaPersonalFinancialStatement.assets.length}</p>
            <p>Liabilities Count: {buyerProfile.sbaPersonalFinancialStatement.liabilities.length}</p>
          </div>
        </div>
      </div>
    );
  } catch (error) {
    console.error('BuyerFinancialAnalysisDebug: Error occurred:', error);
    return (
      <div className="py-4 px-4 sm:py-6 sm:px-6 lg:py-8 lg:px-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-red-900 mb-2">Error in BuyerFinancialAnalysisDebug</h2>
          <p className="text-red-700 mb-4">
            There was an error in the buyer financial analysis component.
          </p>
          <details className="text-sm text-red-600">
            <summary className="cursor-pointer">Error Details</summary>
            <pre className="mt-2 p-2 bg-red-100 rounded text-xs overflow-auto">
              {error instanceof Error ? error.message : String(error)}
            </pre>
          </details>
        </div>
      </div>
    );
  }
}
